import { CellFormat } from '@services/api/deckManagement/graphql/schema/API.ts';
import { ReactNode } from 'react';
import { MenuOption } from 'types/Common.types';
import { ContextMenuEnums } from 'types/Page.types';

/**
 * Enum for border formatting options
 */
export enum BorderType {
  BOTTOM = 'bottom-border-outline',
  TOP = 'top-border-outline',
  LEFT = 'left-border-outline',
  RIGHT = 'right-border-outline',
  ALL = 'all-border-outline',
  NONE = 'no-border-outline',
}

/**
 * Enum for cell color options
 */
export enum CellColor {
  WHITE = 'White',
  GREY = 'Grey',
}

/**
 * Enum for line thickness options
 */
export enum LineThickness {
  THIN = 'thin-line',
  THICK = 'thick-line',
}

/**
 * Enum for text formatting options
 */
export enum TextFormat {
  BOLD = 'bold',
  ITALIC = 'italics',
  UNDERLINE = 'underlined',
}

/**
 * Enum for alignment options
 */
export enum Alignment {
  INDENTATION = 'indentation',
}

/**
 * Enum of valid Deckline formatting fields
 * Used for updates to Deckline properties
 */
export enum DecklineFormattingFields {
  IS_FORMAT_BOLD = 'is_format_bold',
  IS_FORMAT_ITALICS = 'is_format_italics',
  IS_FORMAT_UNDERLINE = 'is_format_underline',
  IS_FORMAT_BORDERLINE = 'is_format_borderline',
  BORDER_BOTTOM_FLAG = 'borderbottomflag',
  BORDER_TOP_FLAG = 'bordertopflag',
  BORDER_LEFT_FLAG = 'borderleftflag',
  BORDER_RIGHT_FLAG = 'borderrightflag',
  BORDER_WEIGHT_FLAG = 'borderweightflag',
  FORMAT_CELL_COLOR = 'format_cell_color',
  FORMAT_CELL_DEFAULT = 'format_cell_default',
}

/**
 * Mapping of formatting fields to their user-friendly audit log labels
 * Used for audit history tracking
 */
export const DECKLINE_FORMATTING_FIELD_AUDIT_LABELS: Record<DecklineFormattingFields, string> = {
  [DecklineFormattingFields.IS_FORMAT_BOLD]: 'Bold',
  [DecklineFormattingFields.IS_FORMAT_ITALICS]: 'Italic',
  [DecklineFormattingFields.IS_FORMAT_UNDERLINE]: 'Underline',
  [DecklineFormattingFields.IS_FORMAT_BORDERLINE]: 'Borderline',
  [DecklineFormattingFields.BORDER_BOTTOM_FLAG]: 'Bottom Border',
  [DecklineFormattingFields.BORDER_TOP_FLAG]: 'Top Border',
  [DecklineFormattingFields.BORDER_LEFT_FLAG]: 'Left Border',
  [DecklineFormattingFields.BORDER_RIGHT_FLAG]: 'Right Border',
  [DecklineFormattingFields.BORDER_WEIGHT_FLAG]: 'Border Weight',
  [DecklineFormattingFields.FORMAT_CELL_COLOR]: 'Cell Color',
  [DecklineFormattingFields.FORMAT_CELL_DEFAULT]: 'Cell Format',
};

/**
 * Props for the DecklineFormattingOptions component
 * Provides formatting controls for deck pages including borders, colors, and text formatting
 */
export interface DecklineFormattingOptionsProps {
  /** Callback function triggered when a formatting option is selected */
  onFormatChange?: (
    formatType: Alignment | BorderType | CellColor | ContextMenuEnums | LineThickness | CellFormat | TextFormat,
    enabled?: boolean,
  ) => void;
  /** Currently selected cell information containing row ID and field name */
  selectedCell?: { rowId: number; field: string } | null;
  /** Whether editing is disabled due to deck lock */
  isEditingDisabled?: boolean;
}

/**
 * Props for the generic FormatMenu component
 * Provides a reusable dropdown menu structure for all formatting options
 */
export interface FormatMenuProps<TValueEnum> {
  /** Value identifier for the toggle button that opens the menu */
  buttonValue: string;
  /** Accessibility label for the toggle button */
  buttonAriaLabel: string;
  /** Icon displayed on the toggle button */
  buttonIcon: ReactNode;
  /** Array of menu options to display in the dropdown */
  options: MenuOption<TValueEnum>[];
  /** Callback function triggered when a menu option is selected */
  onFormatSelect: (formatType: TValueEnum) => void;
  /** Currently selected value to highlight in the menu */
  selectedValue?: TValueEnum;
  /** Optional tooltip string for User clarity of tool on hover. */
  tooltip?: string;
  /** Whether editing is disabled due to deck lock */
  isEditingDisabled?: boolean;
}
